/**
=========================================================
* Soft UI Dashboard PRO React - v4.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import React, { useState, useEffect } from "react";

// react-router-dom components

// @mui material components
import Card from "@mui/material/Card";
import Stack from "@mui/material/Stack";
import Tooltip, { TooltipProps, tooltipClasses } from "@mui/material/Tooltip";
import Button from "@mui/material/Button";
import Icon from "@mui/material/Icon";
import { styled } from "@mui/system";

// Soft UI Dashboard PRO React components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";

// sweetalert2 components
import Swal from "sweetalert2";

// Soft UI Dashboard PRO React example components
import DashboardLayout from "LayoutContainers/DashboardLayout";
import DashboardNavbar from "components/Skill90DashboardNavbar";
import DataTable from "Tables/DataTable";
import CreateMarketsFilter from "Modals/CreaterMarketsFilter";
import CheckObjectDeletion from "Modals/CheckObjectDeletion";

// Soft UI Dashboard PRO React components
import SoftBadge from "components/SoftBadge";

// ProductsList page components
import ActionCell from "views/markets/markets-config/list/components/ActionCell";

import { getData, postData, delData, putData } from "Utils/server";
import { getAuthorizationHeader, getUserData } from "Utils/helpers";

import { useSoftUIController } from "context";
import { Alert, Grid, Snackbar } from "@mui/material";
import SoftSelect from "components/SoftSelect";

// Badges
const disabledTemplate = (
  <SoftBadge variant="contained" color="error" size="xs" badgeContent="Disabled" container />
);
const enabledTemplate = (
  <SoftBadge variant="contained" color="success" size="xs" badgeContent="Enabled" container />
);

/**
 *
 */
const HtmlTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    //backgroundColor: "#f5f5f9",
    backgroundColor: "#000",
    color: "rgba(f, f, f, 1)", //"rgba(0, 0, 0, 0.87)",
    placement: "left",
    maxWidth: 500,
    minWidth: "minContent",
    height: "minContent",
    textAlign: "left",
    fontSize: "0.65erm",
    border: "1px solid #dadde9",
    boxShadow: "0rem 1.25rem 1.6875rem 0rem rgba(0, 0, 0, 0.05)",
    padding: "15px",
  },
}));

let currentData = [];
let providersOptions = null;
let marketsSettings = [];
let oddSettingsOptions = [];
let oddSettingsEditCached = false;
let oddSettingsShowCached = false;

/**
 *
 * @returns
 */
function MarketsSettingsList() {
  const userData = getUserData();
  let defaultStaticOddReactionJson = { value: 0, operator: "+" };
  const [controller, dispatch] = useSoftUIController();
  const { platform, providers } = controller;

  const [updateEnabled, setUpdateEnabled] = useState(false);
  const [showCreateModal, setCreateModal] = useState(false);
  const [showDeleteModal, setDeleteModal] = useState(false);
  const [recordToDelete, setRecordToDelete] = useState(0);
  const [recordSelectedData, setRecordSelectedData] = useState(null);
  const [selectedTemplateOption, setSelectedTemplatesOption] = useState({ value: 0, label: "All" });
  const [selectedTemplateOptionId, setselectedTemplateOptionId] = useState(0);

  const [oddSettingsEdit, setOddSettingsEdit] = useState(false);
  const [oddSettingsShow, setOddSettingsShow] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [oddSettingsAvailable, setSettingsAvailable] = useState(null);
  const [notification, setNotification] = useState(false);
  const [apiResponse, setApiresponse] = useState("success");
  const [apiResponseMsg, setApiResponseMsg] = useState("success");
  const [showDialog, setShowDialog] = useState(false);

  const [loading, setLoading] = useState(true);
  const [dataTableData, setDataTableData] = useState({
    columns: [
      { Header: "selected", accessor: "selected", width: "100px" },
      { Header: "id", accessor: "id", width: "100px" },
      { Header: "name", accessor: "name", width: "100px" },
      { Header: "Markets", accessor: "markets", width: "100px", Cell: renderMarkets },
      {
        Header: "Odds Settings",
        accessor: "odds_filters_id",
        width: "120px",
        Cell: formatOddSetting,
      },
      { Header: "action", accessor: "action", width: "120px" },
    ],

    rows: [],
  });
  const [hiddenColumns, setHiddenColumns] = useState(["selected"]);

  const mixingOptions = [
    { value: 0, label: "No Mixing" },
    { value: 1, label: "Highest Price" },
    { value: 2, label: "Percentage Weight" },
  ];

  const oddsReactions = [
    { value: 0, label: "Do Nothing" },
    { value: 1, label: "Suspend Odds" },
    { value: 2, label: "Set Odds to Limits" },
    { value: 3, label: "Suspend Market" },
  ];

  const marginOptions = [
    { value: 0, label: "No Margin" },
    { value: 1, label: "All odds get same margin (EM)" },
    { value: 2, label: "Highest odds get more margin (MPTO)" },
    { value: 3, label: "Lowest odds get more margin (MPTP)" },
  ];

  const oddsSuspensionType = [
    { value: 0, label: "Never" },
    { value: 1, label: "One Provider Suspend Odd" },
    { value: 2, label: "All Providers Suspend Odd" },
    { value: 3, label: "The Most off the Providers Suspend Odd" },
    { value: 4, label: "Ordered Providers Suspend Odd" },
    { value: 5, label: "Price is higher than the sorted Providers" },
  ];

  const platforms = userData
    ? userData.platforms
      ? userData.platforms.map((platform) => ({
          value: platform.id,
          label: platform.name,
          logo: platform.logo,
          sorting: platform.sorting,
          odds_styles: platform.odds_styles,
        }))
      : []
    : [];

  function renderMarkets({ value, ...rest }) {
    return (
      <HtmlTooltip
        placement="right"
        title={
          <React.Fragment>
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                flexDirection: "row",
              }}
            >
              {value.length} disabled Markets
            </div>
          </React.Fragment>
        }
      >
        <Button className="table-button">
          {value.length}
          <span style={{ paddingLeft: "5px" }}> </span>
          <Icon>info_outlined</Icon>
        </Button>
      </HtmlTooltip>
    );
  }

  function formatOddSetting({ value, ...rest }) {
    return value ? (
      <SoftBox className="odd-setting-menu-holder">
        <SoftTypography
          variant="text"
          color="secondary"
          sx={{ cursor: "pointer", lineHeight: 0, fontSize: "1rem", verticalAlign: "sub" }}
        >
          <Tooltip title="Change Odd Settings" placement="top">
            <Icon
              style={{
                display: rest.row.original.odds_setting_id ? "" : "none",
                color: rest.row.original.odds_setting_id
                  ? rest.row.original.operOddSettings_id
                    ? "#ff000070"
                    : "rgb(203, 12, 159)"
                  : "",
              }}
              onClick={(e) =>
                handleOddSettingSelection(e, {
                  action: "add",
                  data: rest.row.original.id,
                  type: "inplay",
                })
              }
            >
              {rest.row.original.odds_setting_id
                ? rest.row.original.operOddSettings_id
                  ? "playlist_remove_icon"
                  : "playlist_add_circle_icon"
                : "playlist_add_icon"}
            </Icon>
          </Tooltip>
        </SoftTypography>
        <SoftTypography
          variant="text"
          color="secondary"
          style={{
            display:
              rest.row.original.odds_setting_id && !rest.row.original.operOddSettings_id
                ? ""
                : "none",
          }}
          sx={{
            marginLeft: "5px",
            cursor: "pointer",
            lineHeight: 0,
            fontSize: "1rem",
            verticalAlign: "sub",
          }}
        >
          <Tooltip title="Reset Changes" placement="top">
            <Icon
              onClick={(e) =>
                handleOddSettingSelection(e, {
                  action: "remove",
                  data: rest.row.original.id,
                  type: "inplay",
                })
              }
            >
              undo_icon{" "}
            </Icon>
          </Tooltip>
        </SoftTypography>
        <SoftSelect
          className={rest.row.original.operOddSettings_id ? "odd-setting-menu" : "hide"}
          size="small"
          customSmall={true}
          options={oddSettingsOptions}
          value={
            oddSettingsOptions
              ? oddSettingsOptions.find((item) => item.value === rest.row.original.odds_setting_id)
              : null
          }
          onChange={(item) => rowOddSettingsChange(item, rest.row.original, "inplay")}
        ></SoftSelect>
        <HtmlTooltip
          placement="right"
          title={
            <React.Fragment>
              <div style={{}}>
                {rest.row.original.odd_settings_info.map((item, index) => {
                  return (
                    <div key={index} style={{}}>
                      <div style={{ fontWeight: "bold" }}>{item.title}</div>
                      <ul style={{ marginLeft: "5px", paddingLeft: "10px" }}>
                        {item.settings.map((s, i) => {
                          let print = s.value ? (s.key ? s.key + ": " + s.value : s.value) : "";
                          return print == "" ? false : <li key={i}>{print}</li>;
                        })}
                      </ul>
                    </div>
                  );
                })}
              </div>
            </React.Fragment>
          }
        >
          <Button
            sx={{
              maxWidth: "15px",
              width: "15px",
              minWidth: "10px",
              marginRight: "5px",
              display: rest.row.original.odds_setting_id ? "none" : "",
            }}
          >
            <Icon>info_outlined</Icon>
          </Button>
        </HtmlTooltip>

        <SoftTypography
          variant="text"
          color="secondary"
          sx={{
            cursor: "pointer",
            lineHeight: 0,
            fontSize: "1rem",
            verticalAlign: "sub",
            marginRight: "5px",
            display: oddSettingsEditCached
              ? rest.row.original.odds_setting_id
                ? "none"
                : ""
              : "none",
          }}
        >
          <Tooltip title="Edit Filter's Odd Settings" placement="top">
            <Icon
              onClick={(e) =>
                handleOddSettingSelection(e, {
                  action: "edit",
                  data: rest.row.original.id,
                  type: "inplay",
                })
              }
            >
              edit
            </Icon>
          </Tooltip>
        </SoftTypography>
        <SoftTypography
          variant="text"
          color="secondary"
          sx={{
            cursor: "pointer",
            lineHeight: 0,
            fontSize: "1rem",
            verticalAlign: "sub",
            display: oddSettingsEditCached
              ? rest.row.original.odds_setting_id
                ? "none"
                : ""
              : "none",
          }}
        >
          <Tooltip title="Delete Filter's Odd Settings" placement="top">
            <Icon
              onClick={(e) =>
                handleOddSettingSelection(e, {
                  action: "delete",
                  data: rest.row.original.id,
                  type: "inplay",
                })
              }
            >
              delete_outlined
            </Icon>
          </Tooltip>
        </SoftTypography>
      </SoftBox>
    ) : (
      <SoftBox className="odd-setting-menu-holder">
        <SoftTypography
          variant="text"
          color="secondary"
          sx={{
            cursor: "pointer",
            lineHeight: 0,
            fontSize: "1rem",
            verticalAlign: "sub",
            display: oddSettingsEditCached ? "" : "none",
          }}
        >
          <Tooltip
            title={
              rest.row.original.odds_setting_id
                ? "Change Odd Settings"
                : "Add Filter's Odd Settings"
            }
            placement="top"
          >
            <Icon
              style={{
                color: rest.row.original.odds_setting_id
                  ? "rgb(203, 12, 159)"
                  : rest.row.original.operOddSettings_id
                  ? "#ff000070"
                  : "",
              }}
              onClick={(e) =>
                handleOddSettingSelection(e, {
                  action: "add",
                  data: rest.row.original.id,
                  type: "inplay",
                })
              }
            >
              {rest.row.original.odds_setting_id
                ? "playlist_add_circle_icon"
                : rest.row.original.operOddSettings_id
                ? "playlist_remove_icon"
                : "playlist_add_icon"}
            </Icon>
          </Tooltip>
        </SoftTypography>
        <SoftTypography
          variant="text"
          color="secondary"
          sx={{
            cursor: "pointer",
            lineHeight: 0,
            fontSize: "1rem",
            verticalAlign: "sub",
            display:
              oddSettingsShowCached && !oddSettingsEditCached && !rest.row.original.odds_setting_id
                ? ""
                : "none",
          }}
        >
          None
        </SoftTypography>
        <SoftTypography
          variant="text"
          color="secondary"
          style={{
            display:
              rest.row.original.odds_setting_id && !rest.row.original.operOddSettings_id
                ? ""
                : "none",
          }}
          sx={{
            marginLeft: "5px",
            cursor: "pointer",
            lineHeight: 0,
            fontSize: "1rem",
            verticalAlign: "sub",
          }}
        >
          <Tooltip title="Reset Changes" placement="top">
            <Icon
              onClick={(e) =>
                handleOddSettingSelection(e, {
                  action: "remove",
                  data: rest.row.original.id,
                  type: "inplay",
                })
              }
            >
              undo_icon{" "}
            </Icon>
          </Tooltip>
        </SoftTypography>
        <SoftSelect
          className={rest.row.original.operOddSettings_id ? "odd-setting-menu" : "hide"}
          size="small"
          customSmall={true}
          options={oddSettingsOptions}
          value={
            oddSettingsOptions
              ? oddSettingsOptions.find(
                  (item) => item.value === value || item.value === rest.row.original.odds_setting_id
                )
              : null
          }
          onChange={(item) => rowOddSettingsChange(item, rest.row.original, "inplay")}
        ></SoftSelect>
      </SoftBox>
    );
  }

  function handleOddSettingSelection(e, { action, data }) {
    e.preventDefault();

    setUpdateEnabled(false);
    setHiddenColumns([]);
    setDataTableData({ columns: [], rows: [] });
    let row = currentData.rows.find((row) => row.id == data);
    let sendSignal = false;

    if (action == "edit") {
      if (row) {
        row.operOddSettings_id = row.operOddSettings_id ? false : true;
        if (row.operOddSettings_id) {
          row.odds_setting_id = row.odds_filters_id;
        } else {
          if (row.odds_filters_id) {
            delete row.odds_setting_id;
            sendSignal = true;
          }
        }
      }
    }

    if (action == "add") {
      if (row) {
        if (row[`odds_setting_id`] == row[`odds_filters_id`] && row[`odds_setting_id`] != null) {
          delete row[`odds_setting_id`];
          sendSignal = true;
        }
        row[`operOddSettings_id`] = row[`operOddSettings_id`] ? false : true;
      }
    }

    if (action == "remove") {
      if (row) {
        delete row[`odds_setting_id`];
        delete row[`operOddSettings_id`];
        sendSignal = true;
      }
    }

    if (action == "delete") {
      if (row) {
        row[`odds_filters_id`] = null;
        row[`odds_setting_id`] = null;
        row[`operOddSettings_id`] = false;
        sendSignal = true;
      }
    }

    if (sendSignal) handleSignals(new Event("OddSettingsChange"), row.id, { original: row });

    if (marketsSettings.length > 0) setUpdateEnabled(true);
    setHiddenColumns(["selected"]);
    setDataTableData(currentData);
  }

  const getMarketsSettings = () => {
    return marketsSettings;
  };

  const handleUpdate = () => {
    Swal.fire({
      title: "We proccess your request! ",
      html: "Wait for few seconds...",
      timerProgressBar: true,
      didOpen: async () => {
        Swal.showLoading();
        const marketsFiltersEndPoint = `/api/v1/marketsfilters/platform/${platform.value}/config`;
        const marketsFiltersVar = getMarketsSettings();

        const response = await putData(marketsFiltersEndPoint, marketsFiltersVar, {
          Authorization: getAuthorizationHeader(),
        });

        if (!response) {
          Swal.clickDeny();
        } else {
          if (response.status === 200) {
            const marketsFiltersEndPoint = `/api/v1/marketsfilters/platform/${platform.value}`;
            const marketsFiltersVar = getMarketsSettings();
            if (marketsFiltersVar.length > 0) {
              marketsFiltersVar.map((m) => {
                m.platform_id = platform.value;
                m.markets_filters_config_id = m.filter_id;
                m.provider_id = null;
                m.market_id = null;
                m.template_id = null;
                m.sport_id = null;
                delete m.name;
                delete m.filter_id;
                return m;
              });

              const response = await putData(marketsFiltersEndPoint, marketsFiltersVar, {
                Authorization: getAuthorizationHeader(),
              });
            }
            Swal.clickConfirm();
          }
        }
      },
      willClose: () => {},
      allowOutsideClick: false,
    }).then((result) => {
      /* Read more about handling dismissals below */
      if (result.dismiss === Swal.DismissReason.timer) {
        //console.log("I was closed by the timer");
        //routeChange();
      }

      setUpdateEnabled(true);
      setUpdateEnabled(false);
      resetData();
      setShowDialog(false);
      if (result.isConfirmed) {
        Swal.fire("Request Sent!", `Live Data sent to the platform`, "success");
      }
      if (result.isDenied) {
        Swal.fire(
          "Error!",
          `Live Data didn't send to the platform. Please try again later!`,
          "error"
        );
      }
    });
  };

  const handleCancelEdit = async () => {
    if (marketsSettings.length > 0) {
      Swal.fire({
        title: `Unsaved Data!`,
        html:
          "You are going Lose some filtered Markets you have already set.</br></br>" +
          marketsSettings.length +
          " Market filters have been changed. </br></br> Are you Sure?",
        showDenyButton: true,
        showConfirmButton: true,
        confirmButtonText: "Yes, Cancel them!",
        denyButtonText: "Cancel",
        icon: "warning",
        confirmButtonColor: "#87cb16",
        denyButtonColor: "#ff4a55",
      }).then(async (result) => {
        if (result.isConfirmed) {
          //console.log("Modal confirmed");
          setUpdateEnabled(true);
          setUpdateEnabled(false);
          resetData();
          setShowDialog(false);
        }
      });
    } else {
      //console.log("Nothing have been changed");
    }
  };

  /**
   *
   */
  const handleInformParent = ({ key, value, extra }) => {
    if (key == "refresh") {
      initData();
    }
    if (key == "selectOne") {
      setUpdateEnabled(false);
      setHiddenColumns([]);
      setDataTableData({ columns: [], rows: [] });

      let dataRow = currentData.rows.find((item) => item.id == value);
      dataRow.selected = extra;
      let rows = selectedRows;
      let rowExist = rows.findIndex((item) => item.id == value);
      if (rowExist > -1) {
        if (!extra) {
          rows.splice(rowExist, 1);
          setSelectedRows(rows);
        }
      } else {
        if (extra) {
          rows.push(dataRow);
          setSelectedRows(rows);
        }
      }
      if (marketsSettings.length > 0) setUpdateEnabled(true);

      setHiddenColumns(["selected"]);
      setDataTableData(currentData);
    }

    if (key == "selectAll") {
      setUpdateEnabled(false);
      setHiddenColumns([]);
      setDataTableData({ columns: [], rows: [] });
      let dataRows = currentData.rows;
      if (extra == "all") {
        dataRows = value;
        for (let index = 0; index < dataRows.length; index++) {
          const dataRow = dataRows[index];
          dataRow.selected = true;
        }
        setSelectedRows(dataRows);
      } else if (extra == "none") {
        dataRows = value;
        for (let index = 0; index < dataRows.length; index++) {
          const dataRow = dataRows[index];
          dataRow.selected = false;
        }
        setSelectedRows([]);
      }
      if (marketsSettings.length > 0) setUpdateEnabled(true);

      setHiddenColumns(["selected"]);
      setDataTableData(currentData);
    }
  };

  /**
   *
   */
  const handleCreateRecord = () => {
    setCreateModal(true);
  };

  /**
   *
   * @param {*} e
   * @param {*} id
   */
  const handleOpenModal = (e, id) => {
    setRecordToDelete(id);
    setDeleteModal(true);
  };

  /**
   *
   * @param {*} update
   * @param {*} id
   */
  const handleDeleteCloseModal = async (update, id) => {
    if (update) {
      let idToSend = id;
      function returnId() {
        return idToSend;
      }
      let selectedPlatformId = platform.value;
      Swal.fire({
        title: "We proccess your request! ",
        html: "Please Wait a few seconds...",
        timerProgressBar: true,
        didOpen: async () => {
          Swal.showLoading();
          let setTemplatesEndpoint = `/api/v1/marketsfilters/platform/${selectedPlatformId}/config/${returnId()}`;
          let response = await delData(setTemplatesEndpoint, {
            Authorization: getAuthorizationHeader(),
          });

          if (!response) {
            Swal.clickDeny();
          } else {
            if (response.status === 200) {
              deleteRecord(returnId());
              initData();

              Swal.clickConfirm();
            }
          }
        },
        willClose: () => {},
        allowOutsideClick: false,
      }).then((result) => {
        /* Read more about handling dismissals below */
        setDeleteModal(false);
        setRecordToDelete(0);
        if (result.isConfirmed) {
          Swal.fire("Request Sent!", `Live Data sent to the platform`, "success");
        }
        if (result.isDenied) {
          initData();
          Swal.fire(
            "Error!",
            `Live Data didn't send to the platform. Please try again later!`,
            "error"
          );
        }
      });
    } else {
      //console.log("Delete canceled, do nothing!");
      setDeleteModal(false);
      setRecordToDelete(0);
    }
  };

  const handleOpenEditModal = (e, oddFilterData) => {
    // setRecordSelectedProvider(provider);
    // setRecordSelectedTemplate(templateId.toString());
    // setRecordSelectedTemplateName(templateName.toString());
    setRecordSelectedData(oddFilterData);
    setRecordToDelete(oddFilterData.id);
    setCreateModal(true);
  };

  /**
   *
   * @param {*} e
   * @param {*} id
   */
  const handleCheckCloneAction = async (e, marketConfig) => {
    //console.log("id", template.id);
    let marketConfigData = marketConfig;
    let marketConfigToSend = {
      name: marketConfigData.name,
      status: marketConfigData.status === "Enabled" ? false : true,
      platform_id: marketConfigData.platform_id,
    };

    let selectedPlatforms = {};

    for (let index = 0; index < platforms.length; index++) {
      const platform = platforms[index];
      selectedPlatforms[platform.value] = platform.label;
    }

    if (Object.keys(selectedPlatforms).length > 0) {
      const { value: platform } = await Swal.fire({
        icon: "question",
        title: "Select the Platform you want to copy the selected Template",
        input: "select",
        inputOptions: selectedPlatforms,
        inputPlaceholder: "Select Platform",
        showCancelButton: true,
        confirmButtonColor: "#87cb16",
        cancelButtonColor: "#ff4a55",
        inputValidator: (value) => {
          return new Promise((resolve) => {
            if (value) {
              resolve();
            } else {
              resolve("You need to select a platform or press cancel.");
            }
          });
        },
      });
      if (platform) {
        marketConfig.platform_id = platform * 1;
        handleCloneAction(null, marketConfig);
      }
    } else {
      Swal.fire(
        "Error!",
        `The selected Template you try to copy, can't be applied to any of your available Platforms.`,
        "error"
      );
    }
  };

  const handleCloneAction = async (e, marketConfig) => {
    //console.log("id", template.id);
    let selectedPlatformId = platform.value;
    let marketConfigData = marketConfig;
    let marketConfigToSend = {
      name: marketConfigData.name,
      status: marketConfigData.status === "Enabled" ? false : true,
      platform_id: marketConfigData.platform_id,
      odds_filters_id: marketConfigData.odds_filters_id,
    };

    function returnId() {
      return marketConfigData.id;
    }

    function returnTemplateToSend() {
      return marketConfigToSend;
    }

    Swal.fire({
      title: "We proccess your request! ",
      html: "Please Wait a few seconds...",
      timerProgressBar: true,
      didOpen: async () => {
        Swal.showLoading();
        let setTemplatesEndpoint = `/api/v1/marketsfilters/platform/${selectedPlatformId}/config/${returnId()}/clone`;

        let response = await postData(setTemplatesEndpoint, returnTemplateToSend(), {
          Authorization: getAuthorizationHeader(),
        });

        if (!response) {
          Swal.clickDeny();
        } else {
          if (response.status === 200) {
            Swal.clickConfirm();
          }
        }
      },
      willClose: () => {},
      allowOutsideClick: false,
    }).then((result) => {
      /* Read more about handling dismissals below */
      initData();
      if (result.isConfirmed) {
        Swal.fire("Request Sent!", `Live Data sent to the platform`, "success");
      }
      if (result.isDenied) {
        Swal.fire(
          "Error!",
          `Live Data didn't send to the platform. Please try again later!`,
          "error"
        );
      }
    });
  };

  /**
   *
   * @param {*} update
   * @param {*} name
   */
  const handleCloseModal = async (update) => {
    setRecordToDelete(0);
    if (update) {
      initData();
    } else {
      //console.log("Creation canceled, do nothing!");
    }
    // setFiltersSelection([]);
    //setRecordSelectedProvider(null);
    //setRecordSelectedTemplate("0");
    //setRecordSelectedTemplateName("0");
    setRecordSelectedData(null);
    setRecordToDelete(0);

    setCreateModal(false);
  };

  async function resetData() {
    marketsSettings = [];
    setSelectedRows([]);
    setShowDialog(false);
    initData();
  }

  const handleClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }

    setNotification(false);
  };

  function oddSettingsChange(item, type) {
    for (let index = 0; index < selectedRows.length; index++) {
      const filter = selectedRows[index];
      filter[`odds_setting_id`] = item.value;
      filter.modified = true;
      handleSignals(new Event("OddSettingsChange"), filter.id, { original: filter });
    }
  }

  function rowOddSettingsChange(item, row) {
    row[`odds_setting_id`] = item.value;
    row.modified = true;
    row[`operOddSettings_id`] = false;
    handleSignals(new Event("OddSettingsChange"), row.id, { original: row });
  }

  async function handleSignals(e, id, row) {
    e.preventDefault();
    //console.log(row);
    setUpdateEnabled(false);
    setHiddenColumns([]);
    setDataTableData({ columns: [], rows: [] });

    let dataRow = currentData.rows.find((item) => item.id == row.original.id);

    const marketFilter = {
      filter_id: id,
      odds_filter_config_id: dataRow.odds_filter_config_id,
      odds_setting: dataRow.odds_setting_id,
      name: dataRow.name,
    };

    const marketFilterIndex = marketsSettings.findIndex(
      (item) => item.filter_id === marketFilter.filter_id
    );

    if (!dataRow.hasOwnProperty("odds_setting_id")) {
      if (marketFilterIndex > -1) {
        marketsSettings.splice(marketFilterIndex, 1);
        dataRow.modified = false;
      }
      if (marketsSettings.length > 0) setUpdateEnabled(true);

      if (marketFilterIndex > -1 && marketsSettings.length == 0) {
        setApiresponse("error");
        setApiResponseMsg("There are NO unsaved filters changes.");
        setNotification(true);
        setShowDialog(false);
      }

      setHiddenColumns(["selected"]);
      setDataTableData(currentData);

      return;
    } else {
      dataRow.modified = true;
    }

    setHiddenColumns(["selected"]);
    setDataTableData(currentData);

    if (marketFilterIndex < 0) {
      marketsSettings.push(marketFilter);
    } else {
      marketsSettings[marketFilterIndex] = marketFilter;
    }

    if (marketsSettings.length > 0) {
      setUpdateEnabled(true);
      setApiresponse("success");
      setApiResponseMsg("There are unsaved filters changes.");
      setNotification(true);
      setShowDialog(true);
    }
  }

  /**
   *
   */
  async function initData() {
    if (!platform || platform == "") return;
    if (!providersOptions || providersOptions == "") return;
    setLoading(true);
    let selectedPlatformId = platform.value;
    let getTemplatesEndpoint = `/api/v1/marketsfilters/platform/${selectedPlatformId}`;
    let response = await getData(getTemplatesEndpoint, { Authorization: getAuthorizationHeader() });
    if (response != null) {
            response.data.map((el) => {
        el.id = el.id;
        el.selected = false;
        el.odd_settings_info = [];
        el.markets = el.markets[0] ? el.markets : [];
        el.action = (
          <ActionCell
            handleDeleteAction={(e) => handleOpenModal(e, el.id)}
            handleEditAction={(e) => handleOpenEditModal(e, el)}
            handleCheckCloneAction={(e) => handleCheckCloneAction(e, el)}
          />
        );
        el.odds_filter_setting = oddSettingsOptions.find(
          (item) => item.value == el.odds_filters_id
        );

        if (el.odds_filter_setting) {
          el.oddsLimitReaction = oddsReactions.find(
            (or) => or.value == el.odds_filter_setting.barrier_reaction_type
          );
          el.oddsLimitReaction =
            el.oddsLimitReaction || el.oddsLimitReaction == 0 ? el.oddsLimitReaction.label : "";
          el.lowerlimit = el.odds_filter_setting.min_odd_barrier;
          el.upperlimit = el.odds_filter_setting.max_odd_barrier;
          el.mixingType = mixingOptions.find(
            (mo) => mo.value == el.odds_filter_setting.mixing_odd_type
          );
          el.mixingType = el.mixingType || el.mixingType == 0 ? el.mixingType.label : "";
          el.mixing_providers =
            typeof el.odds_filter_setting.mixing_providers == "string"
              ? el.odds_filter_setting.mixing_providers.replace(/[{,},"]/g, "")
              : el.odds_filter_setting.mixing_providers;
          if (
            el.odds_filter_setting.mixing_providers == "NULL" ||
            !el.odds_filter_setting.mixing_providers
          ) {
            el.odds_filter_setting.mixing_providers = [];
          }
          el.mixingProviders =
            typeof el.odds_filter_setting.mixing_providers == "object"
              ? el.odds_filter_setting.mixing_providers.map((p) => p.name).join(",")
              : "";
          el.factor = el.odds_filter_setting.static_odd_reaction
            ? el.odds_filter_setting.static_odd_reaction.toString()
            : "0";
          el.randomFactor = el.odds_filter_setting.random_odd_reaction
            ? "Random Apply"
            : "Allways Apply";
          el.marginType = marginOptions.find(
            (mo) => mo.value == el.odds_filter_setting.margin_type
          );
          el.marginType = el.marginType || el.marginType == 0 ? el.marginType.label : "";
          el.marginPercent = el.odds_filter_setting.margin_percent;
          el.suspension_type = oddsSuspensionType.find(
            (ost) => ost.value == el.odds_filter_setting.suspension_type
          );
        }

        let listItems = [
          {
            title: "Market Suspension",
            settings: [
              {
                key: "Action",
                value:
                  el.suspension_type || el.suspension_type == 0
                    ? el.suspension_type.label
                    : "No Action",
              },
              {
                key: "Providers Priority for Suspension",
                value: el.suspension_provider
                  ? el.suspension_provider
                      .split(",")
                      .map((sp) => providers.find((p) => p.value == sp)["label"])
                  : "-",
              },
            ],
          },
          {
            title: "Odds Limit Reaction",
            settings: [
              { key: "Action", value: el.oddsLimitReaction },
              { key: "min Price", value: el.lowerlimit },
              { key: "max Price", value: el.upperlimit },
            ],
          },
          {
            title: "Mixing Type",
            settings: [
              { key: "Action", value: el.mixingType },
              {
                key: el.mixingType == "No Mixing" ? null : "Mixing",
                value: el.mixingProviders ? el.mixingProviders : "",
              },
              {
                key: "Mixing Odd Limit",
                value: el.mixing_odd_limit ? el.mixing_odd_limit : "-",
              },
            ],
          },
          {
            title: "Price Factor Behaviour",
            settings: [
              {
                key: "Action",
                value:
                  el.odds_filter_setting &&
                  el.odds_filter_setting.static_odd_reaction_json &&
                  el.odds_filter_setting.static_odd_reaction_json.length == 1 &&
                  JSON.stringify(el.odds_filter_setting.static_odd_reaction_json[0].value) ==
                    JSON.stringify(defaultStaticOddReactionJson.value)
                    ? "Do Nothing"
                    : "Prices will be manipulated",
              },
            ],
          },
          {
            title: "Margin Behaviour",
            settings: [
              {
                key: "Action",
                value: el.marginType,
              },
              {
                key: "Margin",
                value: el.marginPercent ? el.marginPercent + "%" : null,
              },
            ],
          },
        ];

        el.odd_settings_info = listItems;
      });
      //setFiltersSelection(selections);
    } else {
    }

    let data = { ...dataTableData };
    data.rows = response && response.data ? response.data : data.rows;

    data.rows.sort((a, b) => b.id - a.id);

    currentData = { ...data };
    let editOddSettingsEnabled = true;
    let showOddSettingsEnabled = true;

    setOddSettingsEdit(editOddSettingsEnabled);
    oddSettingsEditCached = editOddSettingsEnabled;
    setOddSettingsShow(showOddSettingsEnabled);
    oddSettingsShowCached = showOddSettingsEnabled;

    //console.log("Init Data");
    setDataTableData(data);
    setLoading(false);
  }

  async function fetchOddsSettings() {
    let selectedPlatformId = platform.value;
    let getTemplatesEndpoint = `/api/v1/oddsfilters/platforms/${selectedPlatformId}`;
    let response = await getData(getTemplatesEndpoint, { Authorization: getAuthorizationHeader() });
    if (response != null) {
      oddSettingsOptions = response.data.map((r) => {
        return { value: r.id, label: r.name };
      });
      setSettingsAvailable(oddSettingsOptions);
    }
  }

  /**
   *
   * @param {*} id
   */
  function deleteRecord(id) {
    //console.log("deleteRecord:", id);
    let initData = { ...dataTableData };
    let initDataRows = [...dataTableData.rows];

    let indexToRemove = initDataRows.findIndex((template) => template.id === id);
    initDataRows.splice(indexToRemove, 1);

    initData.rows = initDataRows;
    setDataTableData(initData);
  }

  useEffect(() => {
    if (providers) {
      if (platform) fetchOddsSettings();
      providersOptions = providers;
      initData();
    }
  }, [platform.label, providers]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        key={"topright"}
        open={notification}
        autoHideDuration={6000}
        onClose={handleClose}
      >
        <Alert
          onClose={handleClose}
          severity={`${apiResponse}`}
          sx={{ width: "100%" }}
          className={`api-${apiResponse}-notifications`}
        >
          {apiResponseMsg}
        </Alert>
      </Snackbar>
      <CreateMarketsFilter
        id={recordToDelete}
        data={recordSelectedData}
        showModal={showCreateModal}
        signalCloseModal={handleCloseModal}
      ></CreateMarketsFilter>
      <CheckObjectDeletion
        referenceId={recordToDelete}
        reference={"market"}
        showModal={showDeleteModal}
        signalCloseModal={handleDeleteCloseModal}
      ></CheckObjectDeletion>
      <SoftBox my={3}>
        <Card>
          <SoftBox display="flex" justifyContent="space-between" alignItems="flex-start" p={3}>
            <SoftBox lineHeight={1}>
              <SoftTypography variant="h5" fontWeight="medium">
                Your Markets&apos; Settings
              </SoftTypography>
              <SoftTypography variant="button" fontWeight="regular" color="text">
                Here you can find all the saved Markets Settings you have setup
              </SoftTypography>
            </SoftBox>
            <Stack spacing={1} direction="row">
              <SoftButton variant="gradient" color="info" size="small" onClick={handleCreateRecord}>
                + new Markets Setting
              </SoftButton>
            </Stack>
          </SoftBox>
          <SoftBox p={3} sx={{ paddingTop: "0px !important", zIndex: 3 }}>
            <Grid container spacing={3}>
              <Grid item xs={3} md={3}></Grid>
              <Grid item xs={3} md={6}></Grid>
              <Grid item xs={3} md={3} style={{ textAlign: "right" }}>
                <SoftBox lineHeight={1} sx={{ display: oddSettingsEditCached ? "" : "none" }}>
                  <Button onClick={handleCancelEdit} disabled={updateEnabled ? false : true}>
                    Cancel
                  </Button>
                  <Button onClick={handleUpdate} disabled={updateEnabled ? false : true}>
                    Update
                  </Button>
                </SoftBox>
              </Grid>
            </Grid>
          </SoftBox>
          <SoftBox
            pt={2}
            pl={3}
            pr={3}
            sx={{
              display: oddSettingsEditCached ? (selectedRows.length > 0 ? "" : "none") : "none",
            }}
            className="table-multiselected-action"
          >
            <Grid
              container
              sx={{
                backgroundImage: "linear-gradient(to right, #FFFFFF, #f8f9fa, #ffffff)",
                minHeight: "90px",
                maxHeight: "90px",
                color: "red !important",
                // textAlign: "right !important",
              }}
            >
              <Grid
                item
                mr={2}
                md={8}
                sm={8}
                sx={{
                  textAlign: "right !important",
                  width: "200px !important",
                  maxWidth: "fit-content !important",
                  paddingLeft: "50px !important",
                }}
              >
                <SoftTypography variant="caption"> </SoftTypography>
                <SoftTypography
                  variant="caption"
                  style={{ textAlign: "right !important", lineHeight: 10 }}
                >
                  Apply to the selected Markets Settings:{" "}
                </SoftTypography>
              </Grid>
              <Grid item mr={2} style={{ zIndex: 3 }}>
                <SoftTypography variant="caption">Odds Settings: </SoftTypography>
                <SoftSelect
                  size="small"
                  customSmall={true}
                  options={oddSettingsOptions}
                  onChange={(item) => oddSettingsChange(item)}
                ></SoftSelect>
              </Grid>
            </Grid>
          </SoftBox>
          <DataTable
            table={dataTableData}
            entriesPerPage={{
              defaultValue: 10,
              entries: [5, 10, 15, 20, 25],
            }}
            hiddenColumns={hiddenColumns}
            refreshEnabled={true}
            openBackDrop={loading}
            autoResetPageDefault={true}
            autoResetGlobalFilter={true}
            autoResetSortByDefault={true}
            checkboxSelection={oddSettingsEditCached}
            informParent={handleInformParent}
            canSearch
          />
        </Card>
      </SoftBox>
    </DashboardLayout>
  );
}

export default MarketsSettingsList;
